// Spending guardrails — the pure engine behind cap-derived reward values.
//
// WHY THIS EXISTS (SPENDING_GUARDRAILS_SCOPE.md, Alan, 30 Aug 2026). A parent sets the
// exchange rate during setup but never saw the weekly spending cap, and nothing related
// reward VALUES to it — so a household could ship with a spelling test paying more than a
// whole week's cap. The kid hits the cap on day two, gets blocked, and the parent churns.
//
// Everything here is a pure function over (child, state): no React, no DOM, no state
// mutation except the two functions that explicitly say they mutate a draft inside
// update(). That is deliberate — this is the file the unit harness drives directly, and
// projection maths that can only be tested through a mounted component is maths that
// doesn't get tested.
//
// THE MODEL. A "busy week" is four earning slots:
//   English payout · Maths payout · daily-habits streak reward · one earning task
// split 30/30/20/20 of the cap. The Learn & Earn skew is deliberate and Alan's call:
// L&E is the app's differentiator, so 60% of every child's cap flows through it.
//
// THE RESERVED SLOT ("phantom"). The task slot is reserved whether or not the child
// actually has a task yet, so the first task a parent adds lands the projection exactly on
// the cap instead of blowing through it. Children are seeded with starter tasks, so in
// practice this is edge handling — it fires only if a parent deletes every task.
//
// THE ONE RULE THAT GOVERNS ALL OF IT: values never change without an explicit parent tap.
// Nothing in this file is called on load, on save, or on a timer. Defaults are computed at
// ENTRY time (a child's wizard screen first mounting, a task being added) and shown as
// prefills the parent can freely overwrite; consequences of their overwriting surface
// passively through projectBusyWeekTokens() (the wizard health line, the review page
// badges) and are only ever applied by an explicit Apply / Align / Reprice tap.

import { isUntouchedSeed, penceToTokens, removeUntouchedSeeds, reseedStandardSetFromCap, seedStandardSet, spendingCapPence, stampSeededRewards, tokensToPence } from "./utils.js";
import { isYoungChild } from "./age.js";
import { EARN_SPLIT, MIN_REWARD_TOKENS, uid } from "./constants.js";
// LB-132: the split maths moved to its own module so the SEEDER can use the same definition
// without an import cycle (utils.js cannot import this file — this file imports utils.js).
// Re-exported below, so every existing importer of capTokensFor from here is unaffected.
import { capTokensFor as capTokensForImpl, splitForCap } from "./earnSplit.js";
export { distributeTaskSlot, splitForCap } from "./earnSplit.js";

// The busy-week split. Constants, not literals scattered through the file, because Alan
// asked for the L&E skew to be a one-line change if the balance turns out wrong in the
// wild. The task share is deliberately NOT listed: it's whatever is left after the other
// three round, so the four slots always sum to exactly the cap.
export const SPLIT = EARN_SPLIT;

// Below this a reward stops reading as a reward. A 1-token task is not worth a child's
// attention, and "too low to bother with" is a real failure mode — it just fails quietly
// instead of loudly like an over-cap value does.
export const MIN_TOKENS = MIN_REWARD_TOKENS;

// Flag thresholds for the review page. HIGH_SHARE is per SOURCE, not per week: any single
// thing paying more than 40% of the cap means two of them clear the whole week.
export const HIGH_SHARE = 0.40;
export const LOW_SHARE = 0.02;
export const WARN_UNDER = 0.60;
// Linked tasks are expected to diverge in TOKENS (different caps, same share). They are not
// expected to diverge in SHARE — that's drift by neglect, and this is where it starts.
export const DRIFT_SHARE = 0.10;

const clampMin = (n) => Math.max(MIN_TOKENS, Math.round(n || 0));

// cap off: no guardrail, nothing to project against — see earnSplit.js
export const capTokensFor = capTokensForImpl;

export function subjectPayouts(lc) {
  return {
    english: Math.max(1, (lc && lc.learnEarnPayoutsPerWeek) || 1),
    maths: Math.max(1, (lc && lc.mathsPayoutsPerWeek) || 1),
  };
}

// The best REWARDED outcome a subject can produce in one payout — the full-marks reward of
// whichever enabled game pays most. Pass rewards are deliberately ignored here: the cap
// question is "how much can this child earn if everything goes well", and a child who can
// hit full marks will.
export function bestSubjectReward(lc, subject) {
  if (!lc) return 0;
  const games = subject === "english"
    ? [[lc.spellingEnabled !== false, lc.spellingReward], [lc.vocabEnabled !== false, lc.vocabFullReward]]
    : [[lc.ttstreaksEnabled !== false, lc.ttStreakFullReward], [lc.mathsworkoutEnabled !== false, lc.mathsFullReward]];
  return games.reduce((best, [on, v]) => (on && typeof v === "number" && v > best ? v : best), 0);
}

export function childTasks(state, childId) {
  return (state.activities || []).filter((a) => a.childId === childId);
}

// Suggested starting values for one child at their own cap.
//
// The per-payout division matters: a subject configured for 3 payouts a week at 15 tokens
// each is 45 tokens of cap, not 15. The slot target is the WEEKLY figure, so the per-payout
// reward is the slot divided by the payout allowance.
export function suggestedValues(child, state) {
  const rate = state.settings.exchangeRate > 0 ? state.settings.exchangeRate : 10;
  const capTokens = capTokensFor(child, rate);
  const lc = (state.learningContent || {})[child.id] || {};
  const payouts = subjectPayouts(lc);
  if (capTokens === null) return null;
  // A cap this small can't carry four meaningful slots — every slot would round to 1 or 0.
  // Return the floor set and say so, rather than emitting nonsense the parent has to undo.
  // Delegated (LB-132) so that what the wizard OFFERS and what seeding GIVES are produced by
  // one function rather than two that happen to agree today.
  return splitForCap(capTokens, payouts);
}

// What a NEW task should default to.
//
// First task: the reserved slot, so adding it lands the busy week exactly on the cap.
// Every task after that: the mean of what this child already earns per thing. That rule is
// Alan's and it has a property worth keeping — it self-calibrates. A parent who doubled
// everything gets a doubled default for the next task instead of a formula value that
// looks stingy beside their own numbers; the drift from the cap is then the review page's
// job to flag, not the default engine's job to fight.
export function newTaskDefaultTokens(child, state) {
  const s = suggestedValues(child, state);
  if (!s) return MIN_TOKENS;
  const tasks = childTasks(state, child.id);
  if (tasks.length === 0) return s.taskSlot;
  const lc = (state.learningContent || {})[child.id] || {};
  const payouts = subjectPayouts(lc);
  const vals = [
    child.habitReward ?? state.settings.defaultHabitReward ?? 20,
    bestSubjectReward(lc, "english") || s.englishPerPayout,
    bestSubjectReward(lc, "maths") || s.mathsPerPayout,
    ...tasks.map((t) => t.tokens || 0),
  ].filter((n) => typeof n === "number");
  if (!vals.length) return s.taskSlot;
  return clampMin(vals.reduce((a, b) => a + b, 0) / vals.length);
}

// The configured-generosity view: what this child COULD earn in a busy week, given how the
// parent has set things up. Deliberately not the same question as earnedThisWeekPence() —
// that's what has already happened and is the live cap enforcement's business. This is
// "what did I sign up for", which is the only question the wizard can answer at setup time
// (nothing has happened yet) and the only one worth flagging on the review page.
export function projectBusyWeekTokens(child, state) {
  const rate = state.settings.exchangeRate > 0 ? state.settings.exchangeRate : 10;
  const capTokens = capTokensFor(child, rate);
  const lc = (state.learningContent || {})[child.id] || {};
  const payouts = subjectPayouts(lc);
  const sources = [];

  const englishBest = bestSubjectReward(lc, "english");
  if (englishBest > 0) {
    sources.push({ kind: "english", id: "subject-english", label: "English games", weekly: englishBest * payouts.english, each: englishBest, payouts: payouts.english });
  }
  const mathsBest = bestSubjectReward(lc, "maths");
  if (mathsBest > 0) {
    sources.push({ kind: "maths", id: "subject-maths", label: "Maths games", weekly: mathsBest * payouts.maths, each: mathsBest, payouts: payouts.maths });
  }
  const habit = child.habitReward ?? state.settings.defaultHabitReward ?? 20;
  if ((state.habits || []).some((h) => !h.childId || h.childId === child.id)) {
    sources.push({ kind: "habit", id: "habit-streak", label: "Daily tasks streak", weekly: habit, each: habit, payouts: 1 });
  }
  const tasks = childTasks(state, child.id);
  tasks.forEach((t) => sources.push({ kind: "task", id: t.id, label: t.label, weekly: t.tokens || 0, each: t.tokens || 0, payouts: 1, linkKey: t.linkKey }));
  // Pre-migration only: a household still carrying childId-less activities sees them here
  // so its projection isn't silently short. migrateState() converts these on next load.
  (state.activities || []).filter((a) => !a.childId).forEach((a) => {
    sources.push({ kind: "task", id: a.id, label: a.label, weekly: a.tokens || 0, each: a.tokens || 0, payouts: 1, shared: true });
  });

  const configured = sources.reduce((sum, s) => sum + s.weekly, 0);
  // Reserved slot, only while the child genuinely has nothing: it stops the health line
  // reading "well under the cap — rewards may feel small" at the exact moment the parent is
  // about to add the task the room was left for.
  const suggestion = suggestedValues(child, state);
  const phantom = tasks.length === 0 && suggestion && capTokens !== null
    ? Math.max(0, Math.min(suggestion.taskSlot, capTokens - configured))
    : 0;

  const tokens = configured + phantom;
  return {
    tokens,
    configured,
    phantom,
    capTokens,
    capPence: spendingCapPence(child),
    pence: tokensToPence(tokens, rate),
    sources,
    rate,
    state: capTokens === null ? "uncapped" : tokens > capTokens ? "over" : tokens < capTokens * WARN_UNDER ? "warn" : "ok",
  };
}

// Review-page flag for one source. Share is of the CAP, not of the projection — a household
// whose total is already double the cap shouldn't have every row read as "fine, it's only
// 20% of the total".
export function flagForSource(weeklyTokens, capTokens) {
  if (!capTokens) return "ok";
  const share = weeklyTokens / capTokens;
  if (share > HIGH_SHARE) return "high";
  if (weeklyTokens < MIN_TOKENS || share < LOW_SHARE) return "low";
  return "ok";
}

/* Every value Reprice is allowed to move, as one uniform list.
 *
 * `mult` is how many times a week the value is actually paid — a subject allowed 3 payouts
 * pays its reward three times, a task pays once — so it is what converts a stored value
 * into weekly tokens. Getting this wrong is how a reprice can look right on screen and
 * still breach the cap.
 */
function collectRepriceItems(draft, childId) {
  const child = (draft.profiles || []).find((p) => p.id === childId);
  const lc = (draft.learningContent || {})[childId];
  const payouts = subjectPayouts(lc);
  const items = [];
  if (child) {
    items.push({
      mult: 1,
      value: child.habitReward ?? draft.settings.defaultHabitReward ?? 20,
      apply: (v) => { child.habitReward = v; },
    });
  }
  if (lc) {
    const english = bestSubjectReward(lc, "english");
    if (english > 0) {
      items.push({
        mult: payouts.english,
        value: english,
        apply: (v) => {
          // LB-355 (Alan, 21 Sep: "rebalance spelling pass, too. Fix this everywhere"). The pass
          // reward follows its full reward at the same 60% every other game uses; before this,
          // Rebalance resized spelling's full reward and left its pass at the old token count.
          if (lc.spellingEnabled !== false) { lc.spellingReward = v; lc.spellingPassReward = clampMin(v * 0.6); }
          if (lc.vocabEnabled !== false) { lc.vocabFullReward = v; lc.vocabPassReward = clampMin(v * 0.6); }
        },
      });
    }
    const maths = bestSubjectReward(lc, "maths");
    if (maths > 0) {
      items.push({
        mult: payouts.maths,
        value: maths,
        apply: (v) => {
          if (lc.ttstreaksEnabled !== false) { lc.ttStreakFullReward = v; lc.ttStreakPassReward = clampMin(v * 0.6); }
          if (lc.mathsworkoutEnabled !== false) { lc.mathsFullReward = v; lc.mathsPassReward = clampMin(v * 0.6); }
        },
      });
    }
  }
  (draft.activities || []).filter((a) => a.childId === childId).forEach((task) => {
    items.push({ mult: 1, value: task.tokens || 0, apply: (v) => { task.tokens = v; if (task.seeded && typeof task.seeded === "object") task.seeded.tokens = v; } });
  });
  return items;
}

/* MUTATES the draft passed in — call only from inside update(fn).
 *
 * Proportional scaling, not a reset to suggestions. That is the point: by the time a parent
 * taps Reprice they have expressed a view about what matters in their household (reading
 * pays more than tidying). Discarding that and stamping the formula back over it is the
 * "values changed and I don't know why" failure this feature exists to prevent. Scaling
 * preserves their ordering and fixes only the budget. Scales BOTH directions, so it is also
 * the answer to "rewards may feel small", not a second separate action.
 *
 * WHY THE CORRECTION LOOP (staging bug, 30 Aug 2026). The first version just multiplied
 * every value by capTokens/projected and rounded. On Lucy's real household — cap £8 at 50p
 * a token, so 16 tokens, against 18 tokens of rewards made of 4s and 2s — every single
 * value rounded back to itself: 4 x 0.889 = 3.56 -> 4, 2 x 0.889 = 1.78 -> 2. The function
 * reported success, the toast said "repriced", and nothing whatsoever changed. Small token
 * values and a 2-token floor make naive rounding a no-op exactly when the amounts are
 * small, which is precisely the households most likely to breach a cap.
 *
 * So: start from the proportional guess, then walk single tokens off the items that sit
 * furthest ABOVE their ideal share until the week fits (or add to those furthest below,
 * when growing). Floors are respected, which means a genuinely impossible ask — more
 * earning sources than the cap has tokens for, all already at the 2-token minimum — is
 * reported honestly rather than papered over.
 *
 * Returns { changed, fits, tokens, capTokens } so callers can tell "done" from "done what
 * I could" from "nothing I can do", and say so. Never claim a reprice that didn't happen.
 */
/* The correction loops, extracted (Alan, 30 Aug 2026) so Reprice and Reset share one
 * definition of "fits the cap" instead of two.
 *
 * `vals` is mutated in place; `ideals` is each item's fair share, so the walk always takes
 * from (or gives to) whatever is furthest from where it ought to be. Floors are respected,
 * which is why shrink can stop short: a genuinely impossible ask is reported, not papered
 * over. `weeklyOf` is the one line that must stay right — a value paid three times a week
 * costs three times as much cap as one paid once.
 */
function weeklyOf(items, vals) {
  return vals.reduce((sum, v, idx) => sum + v * items[idx].mult, 0);
}

function shrinkToTarget(items, vals, ideals, target) {
  let guard = 0;
  while (weeklyOf(items, vals) > target && guard++ < 1000) {
    let pick = -1;
    let best = -Infinity;
    items.forEach((item, idx) => {
      if (vals[idx] <= MIN_TOKENS) return;
      const excess = vals[idx] - ideals[idx];
      if (excess > best || (excess === best && pick >= 0 && item.mult > items[pick].mult)) { best = excess; pick = idx; }
    });
    if (pick === -1) break; // everything is on the floor: cannot go lower
    vals[pick] -= 1;
  }
  return weeklyOf(items, vals);
}

function growToTarget(items, vals, ideals, target) {
  let guard = 0;
  while (weeklyOf(items, vals) < target && guard++ < 1000) {
    let pick = -1;
    let worst = Infinity;
    items.forEach((item, idx) => {
      if (weeklyOf(items, vals) + item.mult > target) return;
      const shortfall = vals[idx] - ideals[idx];
      if (shortfall < worst) { worst = shortfall; pick = idx; }
    });
    if (pick === -1) break;
    vals[pick] += 1;
  }
  return weeklyOf(items, vals);
}

export function repriceToCap(draft, childId) {
  const child = (draft.profiles || []).find((p) => p.id === childId);
  if (!child) return { changed: false, fits: false, tokens: 0, capTokens: null };
  const projection = projectBusyWeekTokens(child, draft);
  const target = projection.capTokens;
  if (target === null || projection.configured <= 0) {
    return { changed: false, fits: true, tokens: projection.tokens, capTokens: target };
  }
  const items = collectRepriceItems(draft, childId);
  if (!items.length) return { changed: false, fits: projection.tokens <= target, tokens: projection.tokens, capTokens: target };

  const originals = items.map((i) => i.value);
  const total = items.reduce((sum, i) => sum + i.value * i.mult, 0);
  const ratio = total > 0 ? target / total : 1;
  const ideals = items.map((i) => i.value * ratio);
  const vals = items.map((i) => clampMin(i.value * ratio));

  // Shrink: take tokens off whatever is furthest above its fair share first.
  shrinkToTarget(items, vals, ideals, target);
  // Grow: fill the headroom, cheapest-shortfall first, never stepping over the cap.
  growToTarget(items, vals, ideals, target);

  let changed = false;
  items.forEach((item, idx) => {
    if (vals[idx] !== originals[idx]) { item.apply(vals[idx]); changed = true; }
  });
  let tokens = weeklyOf(items, vals);

  // Alan (7 Sep): "either trim, or tell them to trim." When every task is already at the
  // floor and the week still doesn't fit, drop the lightest UNTOUCHED seeded tasks — the
  // ones the app chose, never one the parent edited — until it does, and report what went.
  // If only parent-edited rows remain and it still doesn't fit, the caller says so plainly.
  const dropped = [];
  if (tokens > target) {
    const candidates = (draft.activities || []).filter((a) => a.childId === childId && isUntouchedSeed(a)).sort((a, b) => (a.tokens || 0) - (b.tokens || 0));
    for (const task of candidates) {
      if (tokens <= target) break;
      draft.activities = draft.activities.filter((a) => a.id !== task.id);
      dropped.push(task.label);
      changed = true;
      tokens = projectBusyWeekTokens(child, draft).tokens;
    }
  }
  return { changed, fits: tokens <= target, tokens, capTokens: target, dropped, over: Math.max(0, tokens - target) };
}

/* MUTATES the draft — call only from inside update(). Writes the §2 suggestions over one
 * child's values. This is what "Reset to suggested" runs, and what the wizard runs once per
 * child at entry; it is never called on load, on save, or on a timer.
 *
 * THE BUG THIS NOW FIXES (Alan, staging, 30 Aug 2026: "reprice and reset produce different
 * figures — one over cap, one on cap"). Reprice iterates until the week fits. Reset used to
 * just stamp the formula and stop, and the formula can overshoot: the task slot is divided
 * between the child's tasks and then floored at MIN_TOKENS, so a child with more tasks than
 * the slot can pay for at the floor lands OVER their own cap. His household is exactly that
 * shape — cap £5 at 4 tokens to the pound leaves a 4-token task slot, and four tasks at the
 * 2-token floor is 8 tokens, i.e. the £1.00 over-cap the review page was reporting straight
 * after a Reset. Independent per-slot rounding can also leave a token or two on the table in
 * the other direction.
 *
 * So Reset now finishes through the same correction walk Reprice uses, and returns the same
 * shape of answer. It is a shrink only, never a grow: the suggestions ARE the target here,
 * and inflating them past what the formula asked for would be inventing numbers the parent
 * never chose. A set that cannot fit even with everything on the floor is reported with
 * fits:false so the caller can say so rather than claim a reset that didn't work.
 */
export function applySuggestedValues(draft, childId) {
  const miss = { applied: false, fits: true, tokens: 0, capTokens: null };
  const child = (draft.profiles || []).find((p) => p.id === childId);
  if (!child) return miss;
  const s = suggestedValues(child, draft);
  if (!s) return miss;
  child.habitReward = s.habit;
  const lc = (draft.learningContent || {})[childId];
  if (lc) {
    if (lc.spellingEnabled !== false) { lc.spellingReward = s.englishPerPayout; lc.spellingPassReward = clampMin(s.englishPerPayout * 0.6); } // LB-355
    if (lc.vocabEnabled !== false) { lc.vocabFullReward = s.englishPerPayout; lc.vocabPassReward = clampMin(s.englishPerPayout * 0.6); }
    if (lc.ttstreaksEnabled !== false) { lc.ttStreakFullReward = s.mathsPerPayout; lc.ttStreakPassReward = clampMin(s.mathsPerPayout * 0.6); }
    if (lc.mathsworkoutEnabled !== false) { lc.mathsFullReward = s.mathsPerPayout; lc.mathsPassReward = clampMin(s.mathsPerPayout * 0.6); }
  }
  const tasks = childTasks(draft, childId);
  if (tasks.length) {
    const each = Math.max(MIN_TOKENS, Math.round(s.taskSlot / tasks.length));
    tasks.forEach((t) => { t.tokens = each; if (t.seeded && typeof t.seeded === "object") t.seeded.tokens = each; });
  }

  // The fit pass. Re-collect from the draft so these are the values just stamped, and give
  // the walk the same proportional fair share Reprice uses, so the tokens come off whatever
  // is furthest above its own share rather than off whichever item happens to be first.
  const target = s.capTokens;
  if (target === null || target === undefined) return { applied: true, fits: true, tokens: 0, capTokens: target ?? null };
  const items = collectRepriceItems(draft, childId);
  if (!items.length) return { applied: true, fits: true, tokens: 0, capTokens: target };
  const vals = items.map((i) => i.value);
  const total = weeklyOf(items, vals);
  const ratio = total > 0 ? target / total : 1;
  const ideals = items.map((i) => i.value * ratio);
  const tokens = shrinkToTarget(items, vals, ideals, target);
  items.forEach((item, idx) => { if (vals[idx] !== item.value) item.apply(vals[idx]); });
  return { applied: true, fits: tokens <= target, tokens, capTokens: target };
}

// Seeds (or re-seeds) every child's standard set from their CURRENT cap and links the
// standard tasks of same-band siblings (LB-128 rider 4, LB-133). This is THE function the
// setup fork calls, on both routes — the fork changes navigation, never data — and the
// review screen calls again when a cap is changed. Per child it delegates to
// reseedStandardSetFromCap, which touches nothing the parent has changed; see there.
//
// Linking (LB-133): children on the same side of AGE_UP_THRESHOLD get the same standard
// set, so their matching standard tasks share a linkKey — the existing linked-edit prompt
// and Spending review drift flags then work across siblings, as they do for copied tasks.
// Children on opposite sides have different sets and nothing to link. Matching is by label
// among rows that still carry their seed stamp; a task the parent has renamed is theirs.
//
// One refinement found while building: two same-band siblings link only when they hold
// the SAME standard set. A £3 cap trims the set to its heaviest two tasks and re-weights
// them, so "Tidy your room" is 25% of a £5 sibling's task slot but 58% of the £3 child's —
// linking those would flag Drifted on a household nobody has touched, and a false drift on
// the first review is worse than no link. Siblings whose caps afford the same set link;
// the trimmed child stays independent until a parent copies to or from them.
export function seedHouseholdFromCaps(draft, childIds) {
  const kids = (draft.profiles || []).filter((p) => !childIds || childIds.includes(p.id));
  let changed = false;
  kids.forEach((p) => { if (reseedStandardSetFromCap(draft, p.id)) changed = true; });
  linkStandardSiblings(draft);
  return changed;
}

export function linkStandardSiblings(draft) {
  const bands = { young: [], older: [] };
  (draft.profiles || []).forEach((p) => bands[isYoungChild(p) ? "young" : "older"].push(p.id));
  const seeded = (a) => a && a.seeded && typeof a.seeded === "object" && a.seeded.label === a.label;
  Object.values(bands).forEach((ids) => {
    if (ids.length < 2) return;
    // Group the band's children by the composition of their seeded set; only identical
    // compositions link (see the note above seedHouseholdFromCaps).
    const composition = (cid) => (draft.activities || []).filter((a) => a.childId === cid && seeded(a)).map((a) => a.label.trim().toLowerCase()).sort().join("|");
    const groups = new Map();
    ids.forEach((cid) => { const c = composition(cid); if (!c) return; if (!groups.has(c)) groups.set(c, []); groups.get(c).push(cid); });
    groups.forEach((members) => {
      if (members.length < 2) return;
      const byLabel = new Map();
      (draft.activities || []).forEach((a) => {
        if (!members.includes(a.childId) || !seeded(a)) return;
        const key = a.label.trim().toLowerCase();
        if (!byLabel.has(key)) byLabel.set(key, []);
        byLabel.get(key).push(a);
      });
      byLabel.forEach((rows) => {
        if (rows.length < 2) return;
        const linkKey = rows.find((r) => r.linkKey)?.linkKey || uid();
        rows.forEach((r) => { if (!r.linkKey) r.linkKey = linkKey; });
      });
    });
  });
}

// ---------------------------- adding a child later (LB-129) ----------------------------
//
// A child added outside setup gets "Set it up for me" as the primary. Behind it: if a
// same-band sibling exists, auto COPIES that sibling — linked, cap-sized, exactly as the
// wizard's pre-staging did — and if none exists it seeds the standard set for the band.
// The review then shows where the setup came from, with a Change link to pick a different
// sibling or the standard set. Copy is preserved as behaviour; it stops being a decision
// the parent makes on a screen with no context.

export function sameBandSiblings(draft, childId) {
  const child = (draft.profiles || []).find((p) => p.id === childId);
  if (!child) return [];
  const young = isYoungChild(child);
  return (draft.profiles || []).filter((p) => p.id !== childId && isYoungChild(p) === young && childTasks(draft, p.id).length > 0);
}

// The source auto would pick: the first same-band sibling with tasks (add order, like the
// wizard walks them), else the standard set.
export function defaultSeedSourceFor(draft, childId) {
  const sib = sameBandSiblings(draft, childId)[0];
  return sib ? { kind: "sibling", siblingId: sib.id } : { kind: "standard" };
}

// Re-seeds `childId` from `source`, touching nothing the parent has changed. The app's own
// rows (seed-stamped, still untouched) are replaced; the parent's rows and reward edits
// stay. Copies carry seed stamps too, so a later source switch can replace them the same
// way, and a linkKey shared with the sibling so the drift machinery sees them as one task.
export function seedChildFromSource(draft, childId, source) {
  const child = (draft.profiles || []).find((p) => p.id === childId);
  if (!child) return false;
  const src = source || defaultSeedSourceFor(draft, childId);
  const sibling = src.kind === "sibling" ? (draft.profiles || []).find((p) => p.id === src.siblingId) : null;
  removeUntouchedSeeds(draft, childId);
  if (!sibling) {
    seedStandardSet(draft, childId);
    child._seedSource = { kind: "standard" };
    return true;
  }
  childTasks(draft, sibling.id).forEach((task) => {
    copyTaskToChildren(draft, task.id, [childId]);
    const copy = (draft.activities || []).find((a) => a.childId === childId && a.linkKey === task.linkKey && !a.seeded);
    if (copy) copy.seeded = { label: copy.label, tokens: copy.tokens };
  });
  if (!(draft.habits || []).some((h) => h.childId === childId)) {
    (draft.habits || []).filter((h) => h.childId === sibling.id).forEach((h) => {
      draft.habits.push({ id: uid(), label: h.label, childId, seeded: { label: h.label } });
    });
  }
  if (sibling.habitThreshold !== undefined) child.habitThreshold = sibling.habitThreshold;
  // Rewards are cap-sized, never carried verbatim: a £3 child must not inherit £5 numbers.
  const rate = draft.settings.exchangeRate > 0 ? draft.settings.exchangeRate : 10;
  const lc = (draft.learningContent || {})[childId] || {};
  const split = splitForCap(capTokensForImpl(child, rate), { english: lc.learnEarnPayoutsPerWeek, maths: lc.mathsPayoutsPerWeek });
  if (split) stampSeededRewards(draft, child, split, true);
  // THE CAP DECIDES HOW MUCH OF THE SIBLING'S SET A CHILD GETS — same rule as the standard
  // set. Two passes, in this order:
  //
  // 1. FIT FIRST. A sibling whose tasks take more than the 20% task slot (a parent added
  //    "Walk the dog" on top of the standard four) copies over as the same proportion, and
  //    with rewards stamped at the full 30/30/20 the child lands over cap — even on the
  //    SAME or a BIGGER cap. The sibling themselves fits because the fit pass shrank their
  //    other slots to make room. Do the same here: the same proportional shrink Reprice
  //    uses, tasks included, down to the floors. Alan, 4 Sep: "why do we leave tasks out
  //    … I set a £10 cap and 2 of 4 were still trimmed" — this was the cause.
  // 2. ONLY THEN drop the smallest copies, and only while the floors still do not fit — the
  //    genuinely-small-cap case (five copies at the floor cost 10 tokens against a £3 cap's
  //    6-token slot). The review says how many.
  let dropped = 0;
  if (split) {
    fitChildToCap(draft, childId);
    for (;;) {
      const p = projectBusyWeekTokens(child, draft);
      if (p.capTokens === null || p.tokens <= p.capTokens) break;
      const copies = (draft.activities || []).filter((a) => a.childId === childId && a.seeded);
      if (copies.length <= 1) break;
      const smallest = copies.slice().sort((a, b) => a.tokens - b.tokens)[0];
      draft.activities = draft.activities.filter((a) => a !== smallest);
      dropped += 1;
      fitChildToCap(draft, childId);
    }
  }
  // The fit is the APP's act, not the parent's: re-snapshot the seeded rewards at their
  // fitted values, so the next re-seed (a cap change on the review) still treats them as
  // untouched and moves them with the cap. Without this a £3 → £10 change left Learn &
  // Earn at the £3 figures and the child under cap.
  const lcAfter = (draft.learningContent || {})[childId];
  if (lcAfter && lcAfter._seededRewards) {
    Object.keys(lcAfter._seededRewards).forEach((k) => {
      if (k === "habitReward") lcAfter._seededRewards[k] = child.habitReward;
      else if (lcAfter[k] !== undefined) lcAfter._seededRewards[k] = lcAfter[k];
    });
  }
  child._seedSource = { kind: "sibling", siblingId: sibling.id, dropped };
  child._seededFor = `${spendingCapPence(child) ?? "off"}|${rate}|${isYoungChild(child) ? "young" : "older"}`;
  return true;
}

// The fit pass on its own — the proportional shrink Reprice and applySuggestedValues use,
// without applySuggestedValues' equal repricing of every task first. Leaves a child that
// already fits untouched.
export function fitChildToCap(draft, childId) {
  const child = (draft.profiles || []).find((p) => p.id === childId);
  if (!child) return false;
  const rate = draft.settings.exchangeRate > 0 ? draft.settings.exchangeRate : 10;
  const target = capTokensForImpl(child, rate);
  if (target === null || target === undefined) return false;
  const items = collectRepriceItems(draft, childId);
  if (!items.length) return false;
  const vals = items.map((i) => i.value);
  const total = weeklyOf(items, vals);
  if (total <= target) return false;
  const ratio = target / total;
  const ideals = items.map((i) => i.value * ratio);
  shrinkToTarget(items, vals, ideals, target);
  items.forEach((item, idx) => { if (vals[idx] !== item.value) item.apply(vals[idx]); });
  return true;
}

// Copy one child's task to one or more siblings, sized to each sibling's own cap.
// MUTATES the draft. Same task, same linkKey, different token value — sharing a link is
// what lets the review page tell "deliberately different because their caps differ" apart
// from "one of them was edited and the other forgotten".
export function copyTaskToChildren(draft, taskId, targetChildIds) {
  const source = (draft.activities || []).find((a) => a.id === taskId);
  if (!source) return 0;
  if (!source.linkKey) source.linkKey = uid();
  const sourceChild = (draft.profiles || []).find((p) => p.id === source.childId);
  const sourceProjection = sourceChild ? projectBusyWeekTokens(sourceChild, draft) : null;
  const sourceShare = sourceProjection && sourceProjection.capTokens
    ? (source.tokens || 0) / sourceProjection.capTokens
    : null;
  let made = 0;
  targetChildIds.forEach((cid) => {
    if (cid === source.childId) return;
    // Already linked for this child — copying again would just duplicate the row.
    if ((draft.activities || []).some((a) => a.childId === cid && a.linkKey === source.linkKey)) return;
    const target = (draft.profiles || []).find((p) => p.id === cid);
    if (!target) return;
    const targetCap = capTokensFor(target, draft.settings.exchangeRate > 0 ? draft.settings.exchangeRate : 10);
    const tokens = sourceShare !== null && targetCap
      ? clampMin(sourceShare * targetCap)
      : clampMin(source.tokens || MIN_TOKENS);
    draft.activities.push({
      id: uid(),
      label: source.label,
      tokens,
      childId: cid,
      requirePhoto: !!source.requirePhoto,
      linkKey: source.linkKey,
    });
    made += 1;
  });
  return made;
}

// Linked tasks whose SHARE of their own child's cap has drifted apart. Uniform values
// across different caps — which is exactly what the global-activity migration produces —
// must not flag on day one, hence the values-differ precondition: a migrated set is
// identical in tokens, so it stays quiet until a parent actually edits one side.
export function linkedDrift(state, task) {
  if (!task.linkKey) return null;
  const siblings = (state.activities || []).filter((a) => a.linkKey === task.linkKey && a.id !== task.id);
  if (!siblings.length) return null;
  const rate = state.settings.exchangeRate > 0 ? state.settings.exchangeRate : 10;
  const shareOf = (t) => {
    const child = (state.profiles || []).find((p) => p.id === t.childId);
    const cap = child ? capTokensFor(child, rate) : null;
    return cap ? (t.tokens || 0) / cap : null;
  };
  const mine = shareOf(task);
  if (mine === null) return null;
  for (const sib of siblings) {
    const theirs = shareOf(sib);
    if (theirs === null) continue;
    const valuesDiffer = (sib.tokens || 0) !== (task.tokens || 0);
    if (valuesDiffer && Math.abs(mine - theirs) > DRIFT_SHARE) {
      const child = (state.profiles || []).find((p) => p.id === sib.childId);
      return { sibling: sib, siblingChild: child, mineShare: mine, theirShare: theirs };
    }
  }
  return null;
}

// MUTATES the draft: bring a linked sibling onto the same share of THEIR cap.
export function alignLinkedTask(draft, taskId, siblingTaskId) {
  const task = (draft.activities || []).find((a) => a.id === taskId);
  const sib = (draft.activities || []).find((a) => a.id === siblingTaskId);
  if (!task || !sib) return false;
  const rate = draft.settings.exchangeRate > 0 ? draft.settings.exchangeRate : 10;
  const myChild = (draft.profiles || []).find((p) => p.id === task.childId);
  const sibChild = (draft.profiles || []).find((p) => p.id === sib.childId);
  const myCap = myChild ? capTokensFor(myChild, rate) : null;
  const sibCap = sibChild ? capTokensFor(sibChild, rate) : null;
  if (!myCap || !sibCap) return false;
  sib.tokens = clampMin(((task.tokens || 0) / myCap) * sibCap);
  if (sib.seeded && typeof sib.seeded === "object") sib.seeded.tokens = sib.tokens;
  return true;
}
